<script lang="ts" setup>
import { computed, onMounted, nextTick, useTemplateRef } from 'vue';
import { $fetch } from '@/plugins/fetch';
import InfoCard from '@/components/InfoCard.vue';
import NumberCard from './NumberCard.vue';
import { useAsyncData } from '@/hooks/async-data';
import PeopleCard from '@/components/PeopleCard.vue';

const groupsWrapperRef = useTemplateRef('groupsWrapper');

// 移动端把横滚容器初始定位到画布中心，使根节点（居中）首屏可见
const centerGroupsScroll = () => {
  const el = groupsWrapperRef.value;

  if (!el || el.scrollWidth <= el.clientWidth) {
    return;
  }

  el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
};

onMounted(() => {
  nextTick(centerGroupsScroll);
});

const { data: securityMember } = useAsyncData(
  'about:security:member',
  () => $fetch('/committee/member/v1/list', {
    data: {
      committeeType: 3,
    },
  }),
);

const { data: managements } = useAsyncData(
  'about:security:management',
  () => $fetch('/committee/member/v1/list', {
    data: {
      committeeType: 6,
    },
  }),
);

const { data: groups } = useAsyncData(
  'about:security:group',
  () => $fetch('/security/committee/workingGroup/v1/list'),
  {
    onAfterHandle: ({ serverContext }) => {
      if (!serverContext) {
        nextTick(centerGroupsScroll);
      }
    },
  },
);

const member1 = computed(() => {
  return securityMember.value?.filter((item: Record<string, string>) => item.weight == '1') ?? [];
});

const member2 = computed(() => {
  return securityMember.value?.filter((item: Record<string, string>) => item.weight == '2') ?? [];
});
</script>

<template>
  <section class="page-about-security-commitee pt-48">
    <div class="container pb-48">
      <h2 class="m-0 text-center text-32 font-medium">{{ $t('about.organizationStructure.structure3.title') }}</h2>

      <InfoCard class="mt-32" title-class="min-w-178" :title="$t('about.organizationStructure.structure3.title')">
        <p class="m-0 text-16 leading-24">{{ $t('about.securityCommitee.description') }}</p>
        <ul class="mx-0 mb-0 mt-16 flex flex-wrap list-none gap-y-32 p-0">
          <li class="w-25% <md:w-full">
            <NumberCard number="1">{{ $t('about.securityCommitee.duty1') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="2">{{ $t('about.securityCommitee.duty2') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="3">{{ $t('about.securityCommitee.duty3') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="4">{{ $t('about.securityCommitee.duty4') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="5">{{ $t('about.securityCommitee.duty5') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="6">{{ $t('about.securityCommitee.duty6') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="7">{{ $t('about.securityCommitee.duty7') }}</NumberCard>
          </li>
          <li class="w-25% <md:w-full">
            <NumberCard number="8">{{ $t('about.securityCommitee.duty8') }}</NumberCard>
          </li>
        </ul>
      </InfoCard>

      <ul class="mx-0 mb-0 mt-48 flex flex-wrap list-none gap-x-56 gap-y-8 p-0 <md:gap-x-0">
        <li class="w-416 flex-shrink-0 <2xl:w-362 <md:w-full" v-for="item in managements" :key="item.id">
          <PeopleCard
            size="large"
            :name="item.name"
            :position="item.position"
            :photo="item.photo"
            :intro="item.intro"
            :resume="item.resume"
          />
        </li>
      </ul>

      <div class="working-groups-wrapper mt-32" v-if="groups?.length" ref="groupsWrapper">
        <div class="working-groups-inner">
          <div class="mt-0 flex-x-center <md:mt-48">
            <h3 class="m-0 h-48 min-w-192 whitespace-nowrap rounded-2 bg-brand-primary px-40 text-center text-16 text-white font-normal leading-48">
              {{ $t('about.securityCommitee.standing.title1') }}
            </h3>
          </div>

          <div class="h-75">
            <svg width="100%" height="100%">
              <defs>
                <linearGradient id="fade-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="60" x2="0" y2="75">
                  <stop offset="0%" stop-color="#1180FF" stop-opacity="1" />
                  <stop offset="100%" stop-color="#1180FF" stop-opacity="0" />
                </linearGradient>
              </defs>
              <!-- Top vertical line -->
              <line x1="50%" y1="0" x2="50%" y2="50" stroke="#1180FF" stroke-width="1" />

              <!-- Horizontal line -->
              <line
                :x1="((1 / groups.length) * 0.5 * 100) + '%'"
                y1="50"
                :x2="((1 - (1 / groups.length) * 0.5) * 100) + '%'"
                y2="50"
                stroke="#1180FF"
                stroke-width="1"
              />

              <!-- Vertical lines to children -->
              <line
                v-for="i in groups.length"
                :key="i"
                :x1="(((i - 1) + 0.5) / groups.length * 100) + '%'"
                y1="50"
                :x2="(((i - 1) + 0.5) / groups.length * 100) + '%'"
                y2="75"
                stroke="url(#fade-gradient)"
                stroke-width="1"
              />
            </svg>
          </div>

          <div class="working-groups h-296 flex justify-between gap-x-16">
            <InfoCard
              tiny
              :title="group.name"
              class="group-card min-w-0 flex-1"
              v-for="group in groups"
              :key="group.id"
            >
              <div class="h-full flex flex-col justify-between gap-y-16">
                <h4 class="my-0 truncate text-16 text-brand-primary font-normal leading-24">{{ $t('about.securityCommitee.group.title1') }}{{ group.leadOrg }}</h4>
                <p class="line-clamp-5 mb-0 mt-8 flex-grow text-16 text-brand-normal leading-24">
                  {{ group.summary }}
                </p>
              </div>
            </InfoCard>
          </div>
        </div>
      </div>

      <p class="tree-scroll-hint m-0 mt-12 text-center text-12 text-brand-gray">{{ $t('about.organizationStructure.scrollHint') }}</p>

      <div class="mt-48">
        <h4
          class="relative m-0 flex items-center justify-between text-center text-24 font-normal leading-48 after:block after:h-2 after:w-full after:bg-[linear-gradient(243.99deg,rgba(91,230,255,0)_0%,rgba(17,128,255,0.5)_100%)] after:opacity-20 after:content-empty before:block before:h-2 before:w-full before:bg-[linear-gradient(116.01deg,rgba(91,230,255,0)_0%,rgba(17,128,255,0.5)_100%)] before:opacity-20 before:content-empty"
        >
          <span class="relative z-1 inline-block flex-shrink-0 px-32">{{ $t('about.securityCommitee.member.title1') }}</span>
        </h4>

        <ul class="mb-0 mt-32 flex flex-shrink flex-wrap list-none gap-16 p-0">
          <li v-for="item in member1" :key="item.id" class="group h-78 w-213 bg-white <md:h-60 <md:w-[calc(50%-8px)]">
            <img
              class="lazyload block size-full transform object-cover transition-all duration-300 group-hover:ml--5% group-hover:mt--2% group-hover:size-110% <md:object-top"
              :data-src="item.photo"
              :alt="item.name"
              :title="item.name"
            >
          </li>
        </ul>

        <h4
          class="relative mb-0 mt-32 flex items-center justify-between text-center text-24 font-normal leading-48 after:block after:h-2 after:w-full after:bg-[linear-gradient(243.99deg,rgba(91,230,255,0)_0%,rgba(17,128,255,0.5)_100%)] after:opacity-20 after:content-empty before:block before:h-2 before:w-full before:bg-[linear-gradient(116.01deg,rgba(91,230,255,0)_0%,rgba(17,128,255,0.5)_100%)] before:opacity-20 before:content-empty"
        >
          <span class="relative z-1 inline-block flex-shrink-0 px-32">{{ $t('about.securityCommitee.member.title2') }}</span>
        </h4>

        <ul class="mb-0 mt-32 flex flex-shrink flex-wrap list-none gap-16 p-0">
          <li v-for="item in member2" :key="item.id" class="group h-78 w-213 bg-white <md:h-60 <md:w-[calc(50%-8px)]">
            <img
              class="lazyload block size-full transform object-cover transition-all duration-300 group-hover:ml--5% group-hover:mt--2% group-hover:size-110% <md:object-top"
              :data-src="item.photo"
              :alt="item.name"
              :title="item.name"
            >
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.page-about-security-commitee {
  .working-groups-wrapper {
    @media (max-width: 1279.9px) {
      padding-right: 10px;
      padding-bottom: 20px;
      padding-left: 10px;
      overflow-x: auto;
    }
  }

  .working-groups-inner {
    @media (max-width: 1279.9px) {
      min-width: 1200px;
    }
  }

  .tree-scroll-hint {
    display: none;

    @media (max-width: 1279.9px) {
      display: block;
    }
  }
}
</style>
